import React, { useState } from 'react';
import { useChartColors, money } from './theme';

const TOP_N = 6;

// Magnitude comparison across long names: horizontal bars, one hue, value labeled
// on each row. A table view stays one click away, because the bars answer "which is
// biggest" and the table answers "exactly how much".
export default function TopClasses({ byClass = {} }) {
  const c = useChartColors();
  const [asTable, setAsTable] = useState(false);
  const rows = Object.entries(byClass)
    .map(([title, v]) => ({ title: title || 'Untitled', ...v }))
    .sort((a, b) => b.revenue_cents - a.revenue_cents);
  const shown = asTable ? rows : rows.slice(0, TOP_N);
  const max = Math.max(1, ...rows.map((r) => r.revenue_cents));

  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-xl p-4" aria-label="Top classes by revenue">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold">Top classes by revenue</h3>
        <button type="button" onClick={() => setAsTable((v) => !v)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
          {asTable ? 'Show chart' : `Show all ${rows.length}`}
        </button>
      </div>

      {rows.length === 0 && <div className="text-xs text-gray-500">No revenue in this period.</div>}

      {!asTable && rows.length > 0 && (
        <ul className="space-y-2.5">
          {shown.map((r) => (
            <li key={r.title}>
              <div className="flex justify-between gap-3 text-xs mb-1">
                <span className="truncate text-gray-700 dark:text-gray-300" title={r.title}>{r.title}</span>
                <span className="tabular-nums text-gray-500 shrink-0">
                  {money(r.revenue_cents)} · {r.seats} seats
                </span>
              </div>
              {/* 4px rounded end anchored to a zero baseline, so length stays the encoding. */}
              <div className="h-2 rounded-r" style={{
                width: `${Math.max(2, (r.revenue_cents / max) * 100)}%`,
                background: c.series,
                borderRadius: '2px 4px 4px 2px',
              }} />
            </li>
          ))}
        </ul>
      )}

      {asTable && (
        <table className="w-full text-xs">
          <thead className="text-gray-500">
            <tr>
              <th scope="col" className="text-left py-1">Class</th>
              <th scope="col" className="text-right">Orders</th>
              <th scope="col" className="text-right">Seats</th>
              <th scope="col" className="text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.title} className="border-t border-gray-100 dark:border-gray-800">
                <td className="py-1">{r.title}</td>
                <td className="text-right tabular-nums">{r.count}</td>
                <td className="text-right tabular-nums">{r.seats}</td>
                <td className="text-right tabular-nums">{money(r.revenue_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
